/**
 * Build & trigger a KML download for a GPS trail.
 *
 * KML opens directly in Google Earth, Google My Maps, Garmin BaseCamp,
 * Maps.me, and most GIS tools — useful for proof-of-visit, SLA disputes,
 * and offline audit backups.
 *
 * Output structure:
 *   • Document name = "<userName> — <fromDate> [→ <toDate>]"
 *   • Folder "Trail" with a single LineString of every ping (chronological).
 *   • Folder "Stops" with one Placemark per detected stop, showing
 *     duration in the title.
 *   • Start (green) and End (red) Placemarks at the trail endpoints.
 *
 * All XML special characters in user-supplied strings are escaped.
 */

const esc = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const coord = (p) => `${p.lng},${p.lat},0`;

const STYLES = `
    <Style id="trail">
      <LineStyle><color>ffd17e25</color><width>4</width></LineStyle>
    </Style>
    <Style id="startPt">
      <IconStyle><color>ff00a047</color>
        <Icon><href>https://maps.google.com/mapfiles/kml/paddle/grn-circle.png</href></Icon>
      </IconStyle>
    </Style>
    <Style id="endPt">
      <IconStyle><color>ff0000ff</color>
        <Icon><href>https://maps.google.com/mapfiles/kml/paddle/red-circle.png</href></Icon>
      </IconStyle>
    </Style>
    <Style id="stopPt">
      <IconStyle><color>ff00a5ff</color>
        <Icon><href>https://maps.google.com/mapfiles/kml/paddle/ylw-circle.png</href></Icon>
      </IconStyle>
    </Style>`;

export const buildTrailKml = ({
  userName,
  fromDate,
  toDate,
  points,
  stops = [],
  totalKm = 0,
}) => {
  if (!Array.isArray(points) || points.length === 0) {
    throw new Error('No points to export.');
  }
  const docName = toDate && toDate !== fromDate
    ? `${userName} — ${fromDate} → ${toDate}`
    : `${userName} — ${fromDate}`;
  const lineCoords = points.map(coord).join('\n          ');
  const first = points[0];
  const last = points[points.length - 1];

  const stopPlacemarks = stops
    .map(
      (s, i) => `
      <Placemark>
        <name>Stop ${i + 1} · ${s.durationMinutes} min</name>
        <description><![CDATA[
          <b>From:</b> ${esc(s.startedAt)}<br/>
          <b>To:</b> ${esc(s.endedAt)}<br/>
          <b>Pings:</b> ${s.count}
        ]]></description>
        <styleUrl>#stopPt</styleUrl>
        <Point><coordinates>${coord(s)}</coordinates></Point>
      </Placemark>`
    )
    .join('');

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${esc(docName)}</name>
    <description><![CDATA[
      <b>${esc(userName)}</b><br/>
      Pings: ${points.length}<br/>
      Distance: ${totalKm.toFixed(2)} km<br/>
      Stops (≥5 min): ${stops.length}
    ]]></description>${STYLES}

    <Folder>
      <name>Trail</name>
      <Placemark>
        <name>${esc(userName)} trail</name>
        <styleUrl>#trail</styleUrl>
        <LineString>
          <tessellate>1</tessellate>
          <coordinates>
          ${lineCoords}
          </coordinates>
        </LineString>
      </Placemark>
      <Placemark>
        <name>Start</name>
        <description>${esc(first.timestamp || '')}</description>
        <styleUrl>#startPt</styleUrl>
        <Point><coordinates>${coord(first)}</coordinates></Point>
      </Placemark>
      <Placemark>
        <name>End</name>
        <description>${esc(last.timestamp || '')}</description>
        <styleUrl>#endPt</styleUrl>
        <Point><coordinates>${coord(last)}</coordinates></Point>
      </Placemark>
    </Folder>

    ${stops.length ? `<Folder><name>Stops</name>${stopPlacemarks}</Folder>` : ''}
  </Document>
</kml>`;
};

/** Trigger a browser download of the given KML string. */
export const downloadKml = (kmlString, filename) => {
  const blob = new Blob([kmlString], {
    type: 'application/vnd.google-earth.kml+xml',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
};
